import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useBrandTokens } from '../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { qVitalDefinitions, type VitalDefinitionRow } from '@helios/data-access';

// ─── Types ───

interface VitalDefinition {
  id: string;
  code: string;
  name: string;
  unit: string;
  otherCode: string;
  valueType: string;
  active: boolean;
  mandatory: boolean;
  sortOrder: number;
  category: string;
}

interface VitalFormData {
  code: string;
  name: string;
  unit: string;
  otherCode: string;
  valueType: string;
  active: boolean;
  mandatory: boolean;
  sortOrder: number;
  category: string;
}

interface VitalFormErrors {
  code?: string;
  name?: string;
}

// ─── Row → UI model ───

function rowToVital(row: VitalDefinitionRow): VitalDefinition {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    unit: row.unit ?? '',
    otherCode: row.other_code ?? '',
    valueType: row.value_type,
    active: row.active === 1,
    mandatory: row.mandatory === 1,
    sortOrder: row.sort_order,
    category: row.category ?? 'primary',
  };
}

function generateId(): string {
  return crypto.randomUUID();
}

// ─── Icons ───

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

// ─── Toggle ───

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          transition: 'transform 150ms ease',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          display: 'block',
        }}
      />
    </button>
  );
}

// ─── Card wrapper ───

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

// ─── CodePill ───

function CodePill({ code }: { code: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-info-bg)',
        color: 'var(--color-info)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '22px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      {code}
    </span>
  );
}

// ─── ActionButton ───

function ActionButton({
  onClick,
  title,
  danger,
  children,
}: {
  onClick: () => void;
  title: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: danger ? 'var(--color-danger)' : 'var(--text-muted)',
        padding: 0,
        transition: 'background 150ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

// ─── Table Grid ───

const TABLE_GRID = '80px 1fr 100px 120px 100px 80px 100px 90px';

const COLUMN_HEADER_STYLE: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

// ─── Vitals Table Card ───

function VitalsTableCard({
  vitals,
  onToggleActive,
  onToggleMandatory,
  onEdit,
  onRequestDelete,
  deleteConfirmId,
  onConfirmDelete,
  onCancelDelete,
  activeCount,
  mandatoryCount,
}: {
  vitals: VitalDefinition[];
  onToggleActive: (id: string) => void;
  onToggleMandatory: (id: string) => void;
  onEdit: (vital: VitalDefinition) => void;
  onRequestDelete: (id: string) => void;
  deleteConfirmId: string | null;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  activeCount: number;
  mandatoryCount: number;
}) {
  return (
    <Card>
      {/* Card header */}
      <div style={{ padding: 'var(--sp-6)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            color: 'var(--text)',
          }}
        >
          Vitals
        </div>
      </div>

      {/* Table header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: TABLE_GRID,
          padding: '0 var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          borderTop: '1px solid var(--border)',
          height: 36,
          alignItems: 'center',
        }}
      >
        <span style={COLUMN_HEADER_STYLE}>Code</span>
        <span style={COLUMN_HEADER_STYLE}>Name</span>
        <span style={COLUMN_HEADER_STYLE}>Unit</span>
        <span style={COLUMN_HEADER_STYLE}>Other Code</span>
        <span style={COLUMN_HEADER_STYLE}>Category</span>
        <span style={COLUMN_HEADER_STYLE}>Active</span>
        <span style={COLUMN_HEADER_STYLE}>Mandatory</span>
        <span style={COLUMN_HEADER_STYLE}>Actions</span>
      </div>

      {/* Table rows */}
      {vitals.map((v) => (
        <div
          key={v.id}
          style={{
            display: 'grid',
            gridTemplateColumns: TABLE_GRID,
            padding: '0 var(--sp-6)',
            borderBottom: '1px solid var(--border)',
            height: 48,
            alignItems: 'center',
          }}
        >
          <CodePill code={v.code} />
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text)',
            }}
          >
            {v.name}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {v.unit}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {v.otherCode || '\u2014'}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              textTransform: 'capitalize',
              color: v.category === 'primary' ? 'var(--color-primary)' : 'var(--text-muted)',
            }}
          >
            {v.category}
          </span>
          <Toggle checked={v.active} onChange={() => onToggleActive(v.id)} />
          <div style={{ opacity: v.active ? 1 : 0.4, pointerEvents: v.active ? 'auto' : 'none' }}>
            <Toggle checked={v.mandatory} onChange={() => onToggleMandatory(v.id)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
            {deleteConfirmId === v.id ? (
              <>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-danger)',
                    fontWeight: 'var(--font-weight-medium)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Delete?
                </span>
                <button
                  onClick={onConfirmDelete}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-danger)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: 'pointer',
                    padding: '2px 4px',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={onCancelDelete}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: 'pointer',
                    padding: '2px 4px',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  No
                </button>
              </>
            ) : (
              <>
                <ActionButton onClick={() => onEdit(v)} title="Edit">
                  <PencilIcon />
                </ActionButton>
                <ActionButton onClick={() => onRequestDelete(v.id)} title="Delete" danger>
                  <TrashIcon />
                </ActionButton>
              </>
            )}
          </div>
        </div>
      ))}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--sp-3) var(--sp-6)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
          }}
        >
          Showing {vitals.length} vitals
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
          }}
        >
          {activeCount} active &bull; {mandatoryCount} mandatory
        </span>
      </div>
    </Card>
  );
}

// ─── About Vitals Card ───

function AboutVitalsCard() {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-info-bg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-5) var(--sp-6)',
        display: 'flex',
        gap: 'var(--sp-4)',
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          color: 'var(--color-info)',
          marginTop: 2,
        }}
      >
        <InfoIcon />
      </div>
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            marginBottom: 'var(--sp-2)',
          }}
        >
          About Vitals Configuration
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-body)',
            color: 'var(--text-muted)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-1)',
          }}
        >
          <div>
            <strong style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}>
              Active:
            </strong>{' '}
            Controls whether this vital appears in patient forms and profiles.
          </div>
          <div>
            <strong style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}>
              Mandatory:
            </strong>{' '}
            When enabled, this vital must be recorded during patient consultations.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Form Field Styles ───

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
  marginBottom: 'var(--sp-1)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  border: '1px solid var(--border-input)',
  borderRadius: 'var(--input-radius)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--input-text)',
  fontFamily: 'var(--font-family)',
  outline: 'none',
  boxSizing: 'border-box' as const,
};

const inputErrorStyle: CSSProperties = {
  ...inputStyle,
  border: '1px solid var(--color-danger)',
};

const errorTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--sp-1)',
};

// ─── Validation ───

function validateVitalForm(data: VitalFormData, existingCodes: string[]): VitalFormErrors {
  const errors: VitalFormErrors = {};
  if (!data.code.trim()) {
    errors.code = 'Code is required';
  } else if (existingCodes.includes(data.code.trim().toUpperCase())) {
    errors.code = 'Code must be unique';
  }
  if (!data.name.trim()) errors.name = 'Name is required';
  return errors;
}

// ─── Vital Form Drawer ───

function VitalFormDrawer({
  open,
  onOpenChange,
  brandTokens,
  mode,
  initialData,
  existingCodes,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  mode: 'create' | 'edit';
  initialData: VitalFormData;
  existingCodes: string[];
  onSave: (data: VitalFormData) => void;
}) {
  const [form, setForm] = useState<VitalFormData>(initialData);
  const [errors, setErrors] = useState<VitalFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      setForm(initialData);
      setErrors({});
      setTouched({});
    }
  }, [open, initialData]);

  function handleBlur(field: string) {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateVitalForm(form, existingCodes));
  }

  function handleSave() {
    const errs = validateVitalForm(form, existingCodes);
    setErrors(errs);
    setTouched({ code: true, name: true });
    if (Object.keys(errs).length > 0) return;
    onSave(form);
    onOpenChange(false);
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'create' ? 'New Vital Definition' : 'Edit Vital Definition'}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave}>
            {mode === 'create' ? 'Create Vital' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        {/* Code */}
        <div>
          <label style={labelStyle}>
            Code <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            type="text"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            onBlur={() => handleBlur('code')}
            placeholder="e.g., D13"
            maxLength={6}
            style={touched.code && errors.code ? inputErrorStyle : inputStyle}
          />
          {touched.code && errors.code && <div style={errorTextStyle}>{errors.code}</div>}
        </div>

        {/* Name */}
        <div>
          <label style={labelStyle}>
            Name <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            onBlur={() => handleBlur('name')}
            placeholder="e.g., Blood Glucose"
            style={touched.name && errors.name ? inputErrorStyle : inputStyle}
          />
          {touched.name && errors.name && <div style={errorTextStyle}>{errors.name}</div>}
        </div>

        {/* Unit */}
        <div>
          <label style={labelStyle}>Unit</label>
          <input
            type="text"
            value={form.unit}
            onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
            placeholder="e.g., mg/dL"
            style={inputStyle}
          />
        </div>

        {/* Other Code */}
        <div>
          <label style={labelStyle}>Other Code</label>
          <input
            type="text"
            value={form.otherCode}
            onChange={(e) => setForm((f) => ({ ...f, otherCode: e.target.value }))}
            placeholder="e.g., BG"
            style={inputStyle}
          />
        </div>

        {/* Category */}
        <div>
          <label style={labelStyle}>Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            style={inputStyle}
          >
            <option value="primary">Primary</option>
            <option value="additional">Additional</option>
          </select>
        </div>

        {/* Active toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              Active
            </div>
            <div
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: 2 }}
            >
              Show this vital in patient forms
            </div>
          </div>
          <Toggle
            checked={form.active}
            onChange={() =>
              setForm((f) => ({
                ...f,
                active: !f.active,
                mandatory: !f.active ? false : f.mandatory,
              }))
            }
          />
        </div>

        {/* Mandatory toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            opacity: form.active ? 1 : 0.5,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              Mandatory
            </div>
            <div
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: 2 }}
            >
              {form.active
                ? 'Require this vital during consultations'
                : 'Enable active first to set mandatory'}
            </div>
          </div>
          <Toggle
            checked={form.mandatory && form.active}
            onChange={() => {
              if (form.active) setForm((f) => ({ ...f, mandatory: !f.mandatory }));
            }}
          />
        </div>
      </div>
    </Drawer>
  );
}

// ─── Main Page ───

export function VitalsDefinitionsStubPage() {
  useOutletContext<{ collapsed: boolean }>();
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();

  // Read from PowerSync
  const { data: vitalRows = [] } = usePowerSyncQuery<VitalDefinitionRow>(qVitalDefinitions());
  const vitals = useMemo(() => vitalRows.map(rowToVital), [vitalRows]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editingVital, setEditingVital] = useState<VitalDefinition | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const activeCount = useMemo(() => vitals.filter((v) => v.active).length, [vitals]);
  const mandatoryCount = useMemo(() => vitals.filter((v) => v.mandatory).length, [vitals]);

  const handleToggleActive = useCallback(
    async (id: string) => {
      if (!db) return;
      const vital = vitals.find((v) => v.id === id);
      if (!vital) return;
      const newActive = vital.active ? 0 : 1;
      // If deactivating, also force mandatory off
      if (newActive === 0) {
        await db.execute(
          `UPDATE vital_definitions SET active = 0, mandatory = 0, updated_at = ? WHERE id = ?`,
          [new Date().toISOString(), id],
        );
      } else {
        await db.execute(`UPDATE vital_definitions SET active = 1, updated_at = ? WHERE id = ?`, [
          new Date().toISOString(),
          id,
        ]);
      }
    },
    [db, vitals],
  );

  const handleToggleMandatory = useCallback(
    async (id: string) => {
      if (!db) return;
      const vital = vitals.find((v) => v.id === id);
      if (!vital || !vital.active) return; // Can't toggle mandatory on inactive vital
      const newMandatory = vital.mandatory ? 0 : 1;
      await db.execute(`UPDATE vital_definitions SET mandatory = ?, updated_at = ? WHERE id = ?`, [
        newMandatory,
        new Date().toISOString(),
        id,
      ]);
    },
    [db, vitals],
  );

  function handleOpenCreate() {
    setEditingVital(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }

  function handleOpenEdit(vital: VitalDefinition) {
    setEditingVital(vital);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }

  function handleRequestDelete(id: string) {
    setDeleteConfirmId(id);
  }

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmId || !db) return;
    await db.execute(`UPDATE vital_definitions SET deleted_at = ?, updated_at = ? WHERE id = ?`, [
      new Date().toISOString(),
      new Date().toISOString(),
      deleteConfirmId,
    ]);
    setDeleteConfirmId(null);
  }, [deleteConfirmId, db]);

  function handleCancelDelete() {
    setDeleteConfirmId(null);
  }

  const handleSaveVital = useCallback(
    async (data: VitalFormData) => {
      if (!db) return;
      const now = new Date().toISOString();
      if (drawerMode === 'create') {
        await db.execute(
          `INSERT INTO vital_definitions (id, code, name, unit, other_code, value_type, active, mandatory, sort_order, category, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            generateId(),
            data.code.trim(),
            data.name.trim(),
            data.unit.trim() || null,
            data.otherCode.trim() || null,
            data.valueType,
            data.active ? 1 : 0,
            data.mandatory && data.active ? 1 : 0,
            data.sortOrder,
            data.category,
            now,
          ],
        );
      } else if (editingVital) {
        await db.execute(
          `UPDATE vital_definitions SET code = ?, name = ?, unit = ?, other_code = ?, value_type = ?, active = ?, mandatory = ?, sort_order = ?, category = ?, updated_at = ? WHERE id = ?`,
          [
            data.code.trim(),
            data.name.trim(),
            data.unit.trim() || null,
            data.otherCode.trim() || null,
            data.valueType,
            data.active ? 1 : 0,
            data.mandatory && data.active ? 1 : 0,
            data.sortOrder,
            data.category,
            now,
            editingVital.id,
          ],
        );
      }
    },
    [db, drawerMode, editingVital],
  );

  const drawerInitialData: VitalFormData = editingVital
    ? {
        code: editingVital.code,
        name: editingVital.name,
        unit: editingVital.unit,
        otherCode: editingVital.otherCode,
        valueType: editingVital.valueType,
        active: editingVital.active,
        mandatory: editingVital.mandatory,
        sortOrder: editingVital.sortOrder,
        category: editingVital.category,
      }
    : {
        code: '',
        name: '',
        unit: '',
        otherCode: '',
        valueType: 'number',
        active: true,
        mandatory: false,
        sortOrder: 10,
        category: 'primary',
      };

  const existingCodes = vitals
    .filter((v) => (editingVital ? v.id !== editingVital.id : true))
    .map((v) => v.code.toUpperCase());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '20px var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            <h1
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Vitals
            </h1>
            <span
              style={{
                height: 22,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-xs)',
                background: 'var(--color-warning-bg)',
                color: 'var(--color-warning-text)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: '22px',
                letterSpacing: '0.12px',
              }}
            >
              Admin only
            </span>
          </div>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            Manage vital signs definitions and configuration
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <Button variant="outline" size="md" leftIcon={<PlusIcon />} onClick={handleOpenCreate}>
            NEW
          </Button>
        </div>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
        }}
      >
        <VitalsTableCard
          vitals={vitals}
          onToggleActive={handleToggleActive}
          onToggleMandatory={handleToggleMandatory}
          onEdit={handleOpenEdit}
          onRequestDelete={handleRequestDelete}
          deleteConfirmId={deleteConfirmId}
          onConfirmDelete={handleConfirmDelete}
          onCancelDelete={handleCancelDelete}
          activeCount={activeCount}
          mandatoryCount={mandatoryCount}
        />

        <AboutVitalsCard />
      </div>

      {/* Vital form drawer */}
      <VitalFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        brandTokens={brandTokens}
        mode={drawerMode}
        initialData={drawerInitialData}
        existingCodes={existingCodes}
        onSave={handleSaveVital}
      />
    </div>
  );
}
